import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, TrendingUp, Users, Target, LogOut, Copy, Mail, ExternalLink, Building2, Send, CheckCircle, Clock, XCircle, Info, Rocket, Lightbulb, Play, BarChart3, AlertCircle, Youtube } from 'lucide-react';
import { toast } from 'sonner';
import Footer, { PlatformDisclaimer } from '@/components/Footer';
import { BrandDisclaimerInline } from '@/components/BrandDisclaimer';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const CreatorDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(location.state?.user || null);
  const [dashboardData, setDashboardData] = useState(null);
  const [creatorMode, setCreatorMode] = useState('existing'); // 'existing' or 'strategy'
  const [hasClaimed, setHasClaimed] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [availableCreators, setAvailableCreators] = useState([]);
  const [showPitchModal, setShowPitchModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [generatedPitch, setGeneratedPitch] = useState('');
  const [generatingPitch, setGeneratingPitch] = useState(false);
  const [referralEmail, setReferralEmail] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [insights, setInsights] = useState(null);
  const [contentIdeas, setContentIdeas] = useState([]);
  const [badgeData, setBadgeData] = useState(null);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [applications, setApplications] = useState([]);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [applicationPitch, setApplicationPitch] = useState('');
  const [recommendedBrands, setRecommendedBrands] = useState([]);
  const [noDataForNiche, setNoDataForNiche] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState('open');
  const [expandedFactors, setExpandedFactors] = useState({});
  const [weeklyInsights, setWeeklyInsights] = useState(null);
  const [loadingWeeklyInsights, setLoadingWeeklyInsights] = useState(false);
  const [outreachList, setOutreachList] = useState([]);
  const [showAddOutreach, setShowAddOutreach] = useState(false);
  const [newOutreach, setNewOutreach] = useState({ brand: '', status: 'to_pitch', dealValue: '', notes: '' });
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [updatingNiche, setUpdatingNiche] = useState(false);
  const [showLinkChannel, setShowLinkChannel] = useState(false);
  const [linkChannelUrl, setLinkChannelUrl] = useState('');
  
  // Onboarding state
  const [onboardingYoutubeUrl, setOnboardingYoutubeUrl] = useState('');
  const [onboardingNiche, setOnboardingNiche] = useState('');
  const [analyzingChannel, setAnalyzingChannel] = useState(false);
  const [analysisStep, setAnalysisStep] = useState('');

  useEffect(() => {
    const checkAuth = async () => {
      // Skip if user passed from AuthCallback
      if (location.state?.user) {
        await loadDashboard();
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
        await loadDashboard();
      } catch (error) {
        console.error('Not authenticated:', error);
        navigate('/');
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  const loadDashboard = async () => {
    try {
      const response = await axios.get(`${API}/creators/dashboard/stats`, {
        withCredentials: true
      });
      setDashboardData(response.data);
      setCreatorMode(response.data.creator_mode || 'existing');
      setHasClaimed(true);
      setLoading(false);
      
      // Check if this is demo data (no real channel connected)
      const creator = response.data.creator;
      const isDemo = !creator?.channel_id || creator?.channel_id === 'demo' || creator?.is_demo === true;
      setIsDemoMode(isDemo);
      
      // Only load analytics data for existing creators
      if (response.data.creator_mode !== 'strategy') {
        // Load campaigns for creator's niche
        loadCampaigns(response.data.creator.niche);
        
        // Load insights
        loadInsights();
        
        // Load content ideas
        loadContentIdeas();
        
        // Load badge data
        loadBadgeData(response.data.creator.creator_id);
        
        // Load applications
        loadApplications();
        
        // Load recommended brands
        loadRecommendedBrands(response.data.creator.creator_id);
        
        // Load weekly insights
        loadWeeklyInsights(response.data.creator.creator_id);
        
        // Set availability status
        setAvailabilityStatus(response.data.creator.availability_status || 'open');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // No claimed profile - user needs to complete onboarding
        setNeedsOnboarding(true);
        setHasClaimed(false);
        setLoading(false);
      } else {
        console.error('Failed to load dashboard:', error);
        // Default to onboarding on any error
        setNeedsOnboarding(true);
        setLoading(false);
      }
    }
  };

  const loadCampaigns = async (niche) => {
    try {
      const response = await axios.get(`${API}/campaigns?niche=${encodeURIComponent(niche)}`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };

  const loadInsights = async () => {
    try {
      const response = await axios.get(`${API}/creators/insights`, {
        withCredentials: true
      });
      setInsights(response.data);
    } catch (error) {
      console.error('Failed to load insights:', error);
    }
  };

  const loadContentIdeas = async () => {
    try {
      const response = await axios.get(`${API}/creators/content-ideas`, {
        withCredentials: true
      });
      setContentIdeas(response.data.content_ideas || []);
    } catch (error) {
      console.error('Failed to load content ideas:', error);
    }
  };

  const loadBadgeData = async (creatorId) => {
    try {
      const response = await axios.get(`${API}/creators/${creatorId}/badge`);
      setBadgeData(response.data);
    } catch (error) {
      console.error('Failed to load badge:', error);
    }
  };

  const copyBadgeCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Badge code copied to clipboard!');
  };

  const loadApplications = async () => {
    try {
      const response = await axios.get(`${API}/creators/applications`, {
        withCredentials: true
      });
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Failed to load applications:', error);
    }
  };

  const openApplicationModal = (campaign) => {
    setSelectedCampaign(campaign);
    setApplicationPitch('');
    setShowApplicationModal(true);
  };

  const submitApplication = async () => {
    if (!applicationPitch.trim()) {
      toast.error('Please write a pitch');
      return;
    }

    try {
      await axios.post(
        `${API}/campaigns/${selectedCampaign.campaign_id}/apply`,
        { pitch: applicationPitch },
        { withCredentials: true }
      );
      toast.success('Application submitted!');
      setShowApplicationModal(false);
      loadApplications();
      loadDashboard();
    } catch (error) {
      console.error('Failed to submit application:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
  };

  const loadRecommendedBrands = async (creatorId) => {
    setLoadingBrands(true);
    try {
      const response = await axios.get(`${API}/creators/${creatorId}/recommended-brands?limit=8`);
      setRecommendedBrands(response.data.recommendations || []);
      setNoDataForNiche(response.data.no_data_for_niche || false);
    } catch (error) {
      console.error('Failed to load recommended brands:', error);
    } finally {
      setLoadingBrands(false);
    }
  };

  const loadWeeklyInsights = async (creatorId) => {
    setLoadingWeeklyInsights(true);
    try {
      const response = await axios.get(`${API}/creators/${creatorId}/weekly-insights`, {
        withCredentials: true
      });
      setWeeklyInsights(response.data);
    } catch (error) {
      console.error('Failed to load weekly insights:', error);
      // Try fallback to public endpoint using niche
      if (dashboardData?.creator?.niche) {
        try {
          const fallbackResponse = await axios.get(`${API}/insights/weekly/${encodeURIComponent(dashboardData.creator.niche)}`);
          setWeeklyInsights(fallbackResponse.data);
        } catch (fallbackError) {
          console.error('Failed to load fallback insights:', fallbackError);
        }
      }
    } finally {
      setLoadingWeeklyInsights(false);
    }
  };

  const generatePitchEmail = async (brand) => {
    setSelectedBrand(brand);
    setGeneratingPitch(true);
    setShowPitchModal(true);
    
    try {
      const response = await axios.post(
        `${API}/creators/${dashboardData.creator.creator_id}/generate-pitch`,
        { brand_name: brand.brand_name },
        { withCredentials: true }
      );
      setGeneratedPitch(response.data);
    } catch (error) {
      console.error('Failed to generate pitch:', error);
      toast.error('Failed to generate pitch email');
    } finally {
      setGeneratingPitch(false);
    }
  };

  const copyPitchToClipboard = () => {
    if (generatedPitch) {
      const fullPitch = `Subject: ${generatedPitch.subject}\n\n${generatedPitch.body}`;
      navigator.clipboard.writeText(fullPitch);
      toast.success('Pitch email copied to clipboard!');
    }
  };

  const updateAvailability = async (newStatus) => {
    try {
      await axios.patch(
        `${API}/creators/me/availability`,
        { status: newStatus },
        { withCredentials: true }
      );
      setAvailabilityStatus(newStatus);
      toast.success(`Availability updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      console.error('Failed to update availability:', error);
      toast.error('Failed to update availability');
    }
  };

  const toggleFactorsExpand = (brandName) => {
    setExpandedFactors(prev => ({...prev, [brandName]: !prev[brandName]}));
  };

  // Outreach Tracker Functions
  const loadOutreachList = () => {
    const saved = localStorage.getItem('sponsiq_outreach');
    if (saved) {
      try {
        setOutreachList(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse outreach data:', e);
      }
    }
  };

  const saveOutreachList = (list) => {
    localStorage.setItem('sponsiq_outreach', JSON.stringify(list));
    setOutreachList(list);
  };

  const addOutreach = () => {
    if (!newOutreach.brand.trim()) {
      toast.error('Please enter a brand name');
      return;
    }
    const entry = {
      id: Date.now(),
      brand: newOutreach.brand.trim(),
      status: newOutreach.status,
      dealValue: newOutreach.dealValue,
      notes: newOutreach.notes,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    const updated = [...outreachList, entry];
    saveOutreachList(updated);
    setNewOutreach({ brand: '', status: 'to_pitch', dealValue: '', notes: '' });
    setShowAddOutreach(false);
    toast.success('Outreach added!');
  };

  const updateOutreachStatus = (id, newStatus) => {
    const updated = outreachList.map(item => 
      item.id === id ? { ...item, status: newStatus, updatedAt: new Date().toISOString() } : item
    );
    saveOutreachList(updated);
    toast.success('Status updated!');
  };

  const deleteOutreach = (id) => {
    const updated = outreachList.filter(item => item.id !== id);
    saveOutreachList(updated);
    toast.success('Outreach removed');
  };

  const getStatusColor = (status) => {
    const colors = {
      'to_pitch': 'bg-slate-100 text-slate-700',
      'pitch_sent': 'bg-blue-100 text-blue-700',
      'negotiation': 'bg-amber-100 text-amber-700',
      'accepted': 'bg-green-100 text-green-700',
      'rejected': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'to_pitch': 'To Pitch',
      'pitch_sent': 'Pitch Sent',
      'negotiation': 'Negotiation',
      'accepted': 'Accepted',
      'rejected': 'Rejected'
    };
    return labels[status] || status;
  };

  // Load outreach on mount
  useEffect(() => {
    loadOutreachList();
  }, []);

  const loadAvailableCreators = async () => {
    try {
      const response = await axios.get(`${API}/creators?limit=10`);
      setAvailableCreators(response.data.filter(c => !c.claimed));
    } catch (error) {
      console.error('Failed to load creators:', error);
    }
  };

  const claimProfile = async (creatorId) => {
    try {
      await axios.post(`${API}/creators/${creatorId}/claim`, {}, {
        withCredentials: true
      });
      toast.success('Profile claimed successfully!');
      loadDashboard();
    } catch (error) {
      console.error('Failed to claim profile:', error);
      toast.error('Failed to claim profile. It may already be claimed.');
    }
  };

  const requestBrand = async (brandId) => {
    try {
      await axios.post(`${API}/creators/request-brand?brand_id=${brandId}`, {}, {
        withCredentials: true
      });
      toast.success('Brand collaboration request sent!');
      loadDashboard();
    } catch (error) {
      console.error('Failed to request brand:', error);
      toast.error(error.response?.data?.detail || 'Failed to request brand');
    }
  };

  const generatePitch = async (brand) => {
    setSelectedBrand(brand);
    setShowPitchModal(true);
    setGeneratingPitch(true);
    setGeneratedPitch('');

    try {
      const response = await axios.post(
        `${API}/creators/generate-pitch`,
        {
          creator_id: dashboardData.creator.creator_id,
          brand_id: brand.brand_id
        },
        { withCredentials: true }
      );
      setGeneratedPitch(response.data.pitch);
    } catch (error) {
      console.error('Failed to generate pitch:', error);
      toast.error('Failed to generate pitch');
      setShowPitchModal(false);
    } finally {
      setGeneratingPitch(false);
    }
  };

  const copyPitch = () => {
    navigator.clipboard.writeText(generatedPitch);
    toast.success('Pitch copied to clipboard!');
  };

  const sendReferral = async () => {
    if (!referralEmail.trim()) {
      toast.error('Please enter an email');
      return;
    }

    try {
      await axios.post(
        `${API}/referrals/create?referred_email=${encodeURIComponent(referralEmail)}`,
        {},
        { withCredentials: true }
      );
      toast.success('Referral sent!');
      setReferralEmail('');
      loadDashboard();
    } catch (error) {
      console.error('Failed to send referral:', error);
      toast.error('Failed to send referral');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/');
    }
  };

  // Niches list for onboarding
  const niches = [
    'Technology & Reviews',
    'Gaming',
    'Beauty & Cosmetics',
    'Fitness & Wellness',
    'Finance & Investing',
    'Food & Cooking',
    'Travel & Vlogs',
    'Education & Learning',
    'Entertainment & Comedy',
    'Music',
    'Fashion & Lifestyle',
    'DIY & Crafts',
    'Business & Entrepreneurship',
    'Health & Wellness',
    'Parenting & Family',
    'Sports',
    'Science & Nature',
    'Art & Design'
  ];

  // Handle niche change for strategy mode
  const handleNicheChange = async (newNiche) => {
    if (!newNiche || updatingNiche) return;
    
    setUpdatingNiche(true);
    try {
      await axios.patch(
        `${API}/creators/me/niche`,
        { niche: newNiche },
        { withCredentials: true }
      );
      toast.success(`Niche updated to ${newNiche}`);
      // Reload dashboard to get updated data
      await loadDashboard();
    } catch (error) {
      console.error('Failed to update niche:', error);
      toast.error('Failed to update niche. Please try again.');
    } finally {
      setUpdatingNiche(false);
    }
  };

  // Handle linking YouTube channel from Strategy Mode
  const validateYouTubeUrl = (url) => {
    if (!url) return false;
    const patterns = [
      /youtube\.com\/@[\w-]+/i,           // youtube.com/@handle
      /youtube\.com\/channel\/[\w-]+/i,    // youtube.com/channel/{id}
      /youtube\.com\/c\/[\w-]+/i,          // youtube.com/c/{name}
      /youtube\.com\/user\/[\w-]+/i,       // youtube.com/user/{name}
    ];
    return patterns.some(pattern => pattern.test(url));
  };

  const handleLinkChannel = async () => {
    if (!linkChannelUrl.trim()) {
      toast.error('Please enter your YouTube channel URL');
      return;
    }

    // Validate URL format
    if (!validateYouTubeUrl(linkChannelUrl)) {
      toast.error('Please enter a valid YouTube channel URL (e.g., youtube.com/@yourchannel)');
      return;
    }

    setAnalyzingChannel(true);
    setAnalysisStep('Verifying channel...');
    
    try {
      // Progress step 1
      setAnalysisStep('Verifying channel exists...');
      await new Promise(r => setTimeout(r, 500));
      
      // Progress step 2
      setAnalysisStep('Fetching channel data...');
      
      // Analyze the YouTube channel
      const response = await axios.post(`${API}/analyze/channel`, {
        youtube_link: linkChannelUrl
      });
      
      // Check if channel was found
      if (!response.data || !response.data.channel_id) {
        toast.error("We couldn't find this channel. Please check the URL.");
        return;
      }
      
      // Progress step 3
      setAnalysisStep('Detecting sponsorship mentions...');
      await new Promise(r => setTimeout(r, 500));
      
      // Store the analyzed channel data
      const analysisData = response.data;
      
      // Update the creator profile with the channel data
      try {
        await axios.post(
          `${API}/creators/claim-analyzed`,
          { 
            channel_id: analysisData.channel_id,
            channel_name: analysisData.channel_name,
            subscriber_count: analysisData.subscriber_count,
            niche: analysisData.primary_niche || analysisData.niche,
            sponsorship_readiness_score: analysisData.sponsorship_readiness_score
          },
          { withCredentials: true }
        );
      } catch (claimError) {
        console.log('Claim endpoint response:', claimError);
      }
      
      toast.success('Channel connected. Analyzing your recent videos...');
      setShowLinkChannel(false);
      setLinkChannelUrl('');
      loadDashboard();
    } catch (error) {
      console.error('Failed to link channel:', error);
      if (error.response?.status === 429) {
        toast.error('Verification temporarily unavailable. Try again later.');
      } else if (error.response?.status === 404) {
        toast.error("We couldn't find this channel. Please check the URL.");
      } else {
        toast.error('Unable to verify channel right now. Please try again.');
      }
    } finally {
      setAnalyzingChannel(false);
      setAnalysisStep('');
    }
  };

  // Handle onboarding - analyze channel or register with niche
  const handleOnboardingSubmit = async () => {
    if (!onboardingYoutubeUrl.trim() && !onboardingNiche) {
      toast.error('Please enter a YouTube channel URL or select a niche');
      return;
    }

    setAnalyzingChannel(true);
    setAnalysisStep('Analyzing your channel...');
    
    try {
      if (onboardingYoutubeUrl.trim()) {
        // Progress step 1
        setAnalysisStep('Scanning channel data...');
        await new Promise(r => setTimeout(r, 800));
        
        // Progress step 2
        setAnalysisStep('Detecting sponsorship mentions...');
        
        // Analyze the YouTube channel
        const response = await axios.post(`${API}/analyze/channel`, {
          youtube_link: onboardingYoutubeUrl
        });
        
        // Progress step 3
        setAnalysisStep('Calculating sponsorship score...');
        await new Promise(r => setTimeout(r, 500));
        
        // Store the analyzed channel data for claiming
        const analysisData = response.data;
        
        // Create or claim the creator profile with the analyzed data
        try {
          await axios.post(
            `${API}/creators/claim-analyzed`,
            { 
              channel_id: analysisData.channel_id,
              channel_name: analysisData.channel_name,
              subscriber_count: analysisData.subscriber_count,
              niche: analysisData.primary_niche || analysisData.niche,
              sponsorship_readiness_score: analysisData.sponsorship_readiness_score
            },
            { withCredentials: true }
          );
        } catch (claimError) {
          // If endpoint doesn't exist, try register-strategy as fallback
          console.log('Claim endpoint not available, proceeding to dashboard');
        }
        
        toast.success('Channel analyzed successfully!');
        setNeedsOnboarding(false);
        loadDashboard();
      } else if (onboardingNiche) {
        // Register as strategy mode creator
        await axios.post(
          `${API}/creators/register-strategy`,
          { niche: onboardingNiche },
          { withCredentials: true }
        );
        toast.success('Profile created successfully!');
        setNeedsOnboarding(false);
        loadDashboard();
      }
    } catch (error) {
      console.error('Onboarding failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze channel. Please try again.');
    } finally {
      setAnalyzingChannel(false);
      setAnalysisStep('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Onboarding Screen - for users who haven't set up their profile
  if (needsOnboarding || !hasClaimed) {
    return (
      <div className="min-h-screen bg-slate-50">
        <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-indigo-600" />
              <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/explore-brands">
                <Button variant="ghost" size="sm">
                  <Building2 className="w-4 h-4 mr-1.5" />
                  Brands
                </Button>
              </Link>
              <span className="text-slate-600 hidden sm:inline">{user?.name}</span>
              <Button onClick={handleLogout} variant="ghost" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </nav>

        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Youtube className="w-8 h-8 text-indigo-600" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-3" style={{fontFamily: 'Manrope'}} data-testid="onboarding-heading">
              Get Your Sponsorship Score
            </h1>
            <p className="text-lg text-slate-600">
              Analyze your channel to discover brands most likely to sponsor your content.
            </p>
          </div>

          <Card className="p-8" data-testid="onboarding-card">
            <div className="space-y-6">
              {/* YouTube URL Input */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Enter your YouTube channel URL
                </Label>
                <Input
                  type="text"
                  placeholder="https://youtube.com/@yourchannel"
                  value={onboardingYoutubeUrl}
                  onChange={(e) => setOnboardingYoutubeUrl(e.target.value)}
                  className="h-12"
                  data-testid="onboarding-youtube-input"
                />
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-slate-500">
                    Supported formats:
                  </p>
                  <div className="text-xs text-slate-400 space-y-0.5">
                    <p>• youtube.com/@MrBeast</p>
                    <p>• youtube.com/channel/UCxxxx</p>
                    <p>• youtube.com/c/channelname</p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-slate-500">or</span>
                </div>
              </div>

              {/* Niche Selection */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Select your content niche
                </Label>
                <Select value={onboardingNiche} onValueChange={setOnboardingNiche}>
                  <SelectTrigger className="h-12" data-testid="onboarding-niche-select">
                    <SelectValue placeholder="Choose your niche..." />
                  </SelectTrigger>
                  <SelectContent>
                    {niches.map((niche) => (
                      <SelectItem key={niche} value={niche}>{niche}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1.5">
                  Don't have a channel yet? Select your niche to explore sponsorship opportunities
                </p>
              </div>

              {/* Submit Button with Enhanced Loading */}
              <Button
                onClick={handleOnboardingSubmit}
                disabled={analyzingChannel || (!onboardingYoutubeUrl.trim() && !onboardingNiche)}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12 font-semibold"
                data-testid="onboarding-submit-btn"
              >
                {analyzingChannel ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {analysisStep || 'Analyzing...'}
                  </>
                ) : (
                  <>
                    Analyze Your Channel
                    <Target className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Explore Brands CTA */}
          <div className="mt-8 text-center">
            <p className="text-slate-600 mb-3">Want to explore sponsors first?</p>
            <Link to="/explore-brands">
              <Button variant="outline" data-testid="explore-brands-btn">
                <Building2 className="w-4 h-4 mr-2" />
                Browse Brand Database
              </Button>
            </Link>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  // Strategy Dashboard View (for users without channels yet)
  if (creatorMode === 'strategy') {
    const strategyData = dashboardData?.strategy_data || {};
    
    return (
      <div className="min-h-screen bg-slate-50">
        <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-indigo-600" />
              <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/explore-brands">
                <Button variant="ghost" size="sm">
                  <Building2 className="w-4 h-4 mr-1.5" />
                  Brands
                </Button>
              </Link>
              <span className="text-slate-600 hidden sm:inline">{user?.name}</span>
              <Button onClick={handleLogout} variant="ghost" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-6 py-12">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">Strategy Mode</Badge>
              <Select 
                value={strategyData.niche || 'Technology'} 
                onValueChange={handleNicheChange}
                disabled={updatingNiche}
              >
                <SelectTrigger 
                  className="w-auto h-7 px-3 text-sm font-medium border-slate-200 bg-white hover:bg-slate-50"
                  data-testid="niche-selector"
                >
                  <SelectValue placeholder="Select niche" />
                </SelectTrigger>
                <SelectContent>
                  {niches.map((niche) => (
                    <SelectItem key={niche} value={niche}>{niche}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {updatingNiche && (
                <span className="text-xs text-slate-500">Updating...</span>
              )}
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}} data-testid="strategy-dashboard-heading">
              Channel Strategy Dashboard
            </h1>
            <p className="text-lg text-slate-600">
              Plan your YouTube channel and discover sponsorship opportunities in your niche
            </p>
          </div>

          {/* Niche Potential Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="p-6 bg-gradient-to-br from-indigo-50 to-white border-indigo-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-indigo-600" />
                </div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Active Sponsors</p>
              </div>
              <p className="text-3xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}} data-testid="active-sponsors">
                {strategyData.niche_potential?.active_sponsors || 0}
              </p>
              <p className="text-sm text-slate-500 mt-1">brands sponsoring in {strategyData.niche}</p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-green-50 to-white border-green-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Opportunity</p>
              </div>
              <p className="text-3xl font-bold text-green-600" style={{fontFamily: 'JetBrains Mono'}} data-testid="opportunity-level">
                {strategyData.niche_potential?.opportunity_level || 'Growing'}
              </p>
              <p className="text-sm text-slate-500 mt-1">sponsorship potential</p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-purple-50 to-white border-purple-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Avg Mentions</p>
              </div>
              <p className="text-3xl font-bold text-purple-600" style={{fontFamily: 'JetBrains Mono'}} data-testid="avg-mentions">
                {strategyData.niche_potential?.avg_mentions || 0}
              </p>
              <p className="text-sm text-slate-500 mt-1">per brand in this niche</p>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Growth Roadmap */}
            <Card className="p-6" data-testid="growth-roadmap">
              <div className="flex items-center gap-2 mb-6">
                <Rocket className="w-5 h-5 text-indigo-600" />
                <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Growth Roadmap</h2>
              </div>
              
              <div className="space-y-4">
                {(strategyData.growth_roadmap || []).map((milestone, index) => (
                  <div key={index} className="flex gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      milestone.status === 'current' ? 'bg-indigo-600 text-white' :
                      milestone.status === 'completed' ? 'bg-green-600 text-white' :
                      'bg-slate-200 text-slate-500'
                    }`}>
                      {milestone.status === 'completed' ? <CheckCircle className="w-4 h-4" /> : index + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-semibold ${milestone.status === 'current' ? 'text-indigo-600' : 'text-slate-900'}`}>
                        {milestone.milestone}
                      </h3>
                      <p className="text-sm text-slate-500">{milestone.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Video Topic Suggestions */}
            <Card className="p-6" data-testid="video-topics">
              <div className="flex items-center gap-2 mb-6">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Video Ideas</h2>
              </div>
              
              <div className="space-y-3">
                {(strategyData.video_topic_suggestions || []).map((topic, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Play className="w-4 h-4 text-amber-600" />
                    </div>
                    <p className="text-slate-700">{topic}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Recommended Brands Section */}
          <Card className="p-6 mt-8" data-testid="recommended-brands-strategy">
            <div className="flex items-center gap-2 mb-6">
              <Building2 className="w-5 h-5 text-indigo-600" />
              <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                Brands Sponsoring in {strategyData.niche || 'Your Niche'}
              </h2>
            </div>
            
            {strategyData.recommended_brands?.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {strategyData.recommended_brands.map((brand, index) => (
                  <div key={index} className="p-4 border border-slate-200 rounded-xl hover:border-indigo-200 hover:shadow-md transition-all">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <span className="text-indigo-600 font-bold">{brand.brand_name?.charAt(0)}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{brand.brand_name}</h3>
                        <p className="text-xs text-slate-500">{brand.niche_category}</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600">
                      <span className="font-medium text-indigo-600">{brand.mention_count}</span> creator mentions
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Still analyzing your niche</h3>
                <p className="text-slate-500 mb-4 max-w-md mx-auto">
                  We're actively discovering brand sponsorships in {strategyData?.niche || 'your niche'}. 
                  Insights will appear as we detect brand mentions.
                </p>
                <p className="text-sm text-slate-400 mb-6">In the meantime, explore what's available:</p>
                <div className="flex justify-center gap-3">
                  <Link to="/explore-brands">
                    <Button variant="outline" size="sm">
                      <Building2 className="w-4 h-4 mr-1.5" />
                      Explore Brands
                    </Button>
                  </Link>
                  <Link to="/discover-creators">
                    <Button variant="outline" size="sm">
                      <Users className="w-4 h-4 mr-1.5" />
                      View Top Creators
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </Card>

          {/* Link YouTube Channel Section */}
          <Card className="p-6 mt-8" data-testid="link-channel-section">
            <div className="flex items-center gap-2 mb-4">
              <Youtube className="w-5 h-5 text-red-600" />
              <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                Link Your YouTube Channel
              </h2>
            </div>
            
            {!showLinkChannel ? (
              <div className="text-center py-4">
                <p className="text-slate-600 mb-4">
                  Already have a YouTube channel? Link it to unlock the full creator dashboard with personalized insights.
                </p>
                <Button 
                  onClick={() => setShowLinkChannel(true)}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="link-channel-btn"
                >
                  <Youtube className="w-4 h-4 mr-2" />
                  Link My Channel
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="channelUrl" className="text-sm font-medium text-slate-700">
                    YouTube Channel URL
                  </Label>
                  <Input
                    id="channelUrl"
                    type="text"
                    placeholder="youtube.com/@yourchannel or youtube.com/channel/..."
                    value={linkChannelUrl}
                    onChange={(e) => setLinkChannelUrl(e.target.value)}
                    className="mt-1"
                    disabled={analyzingChannel}
                    data-testid="channel-url-input"
                  />
                </div>
                
                {analyzingChannel && (
                  <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg">
                    <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-indigo-700 text-sm">{analysisStep}</span>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleLinkChannel}
                    disabled={analyzingChannel || !linkChannelUrl.trim()}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="analyze-channel-btn"
                  >
                    {analyzingChannel ? 'Analyzing...' : 'Analyze & Link Channel'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowLinkChannel(false);
                      setLinkChannelUrl('');
                    }}
                    disabled={analyzingChannel}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Call to Action - Create New Channel */}
          <Card className="p-8 mt-8 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2" style={{fontFamily: 'Manrope'}}>Don't Have a Channel Yet?</h2>
              <p className="text-indigo-100 mb-6">
                Create your YouTube channel and come back to link it for personalized sponsorship insights
              </p>
              <div className="flex justify-center gap-4">
                <Button 
                  variant="secondary" 
                  className="bg-white text-indigo-600 hover:bg-indigo-50"
                  onClick={() => window.open('https://studio.youtube.com', '_blank')}
                >
                  Create YouTube Channel
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Dashboard View (Existing Creator Mode)
  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/explore-brands">
              <Button variant="ghost" size="sm">
                <Building2 className="w-4 h-4 mr-1.5" />
                Brands
              </Button>
            </Link>
            <span className="text-slate-600 hidden sm:inline">{user?.name}</span>
            <Button onClick={handleLogout} variant="ghost" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{fontFamily: 'Manrope'}} data-testid="dashboard-heading">
                  {dashboardData?.creator?.channel_name}
                </h1>
                {isDemoMode && (
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded-full">
                    Example Creator Profile
                  </span>
                )}
              </div>
              <p className="text-lg text-slate-600">
                {dashboardData?.creator?.subscriber_count?.toLocaleString()} subscribers · {dashboardData?.creator?.niche}
                {isDemoMode && <span className="text-amber-600 ml-2">(Demo data)</span>}
              </p>
              {/* Connected Channel Indicator */}
              {dashboardData?.creator?.channel_id && (
                <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                  <Youtube className="w-4 h-4 text-red-500" />
                  <span>Connected: youtube.com/@{dashboardData?.creator?.channel_name?.toLowerCase().replace(/\s+/g, '')}</span>
                  <button 
                    className="text-indigo-600 hover:text-indigo-700 underline text-xs ml-2"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to change your channel? This will reset your dashboard data.')) {
                        setShowLinkChannel(true);
                      }
                    }}
                  >
                    Change channel
                  </button>
                </div>
              )}
            </div>
            
            {/* Availability Status Control */}
            <div className="flex items-center gap-3">
              <Label className="text-sm text-slate-600">Availability:</Label>
              <Select value={availabilityStatus} onValueChange={updateAvailability}>
                <SelectTrigger className="w-44" data-testid="availability-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Open to Sponsorships
                    </div>
                  </SelectItem>
                  <SelectItem value="busy">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-500" />
                      Busy
                    </div>
                  </SelectItem>
                  <SelectItem value="not_available">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      Not Available
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Link Channel Modal (when changing channel) */}
        {showLinkChannel && (
          <Card className="mb-6 p-6 border-2 border-indigo-200 bg-indigo-50/50" data-testid="change-channel-section">
            <div className="flex items-center gap-2 mb-4">
              <Youtube className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold text-slate-900">Link a Different Channel</h3>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="newChannelUrl" className="text-sm font-medium text-slate-700">
                  YouTube Channel URL
                </Label>
                <Input
                  id="newChannelUrl"
                  type="text"
                  placeholder="https://youtube.com/@yourchannel"
                  value={linkChannelUrl}
                  onChange={(e) => setLinkChannelUrl(e.target.value)}
                  className="mt-1 bg-white"
                  disabled={analyzingChannel}
                  data-testid="new-channel-url-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Accepts: youtube.com/@handle, youtube.com/channel/ID, youtube.com/c/name
                </p>
              </div>
              
              {analyzingChannel && (
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-indigo-200">
                  <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-indigo-700 text-sm">{analysisStep}</span>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button
                  onClick={handleLinkChannel}
                  disabled={analyzingChannel || !linkChannelUrl.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {analyzingChannel ? 'Verifying...' : 'Verify & Link Channel'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowLinkChannel(false);
                    setLinkChannelUrl('');
                  }}
                  disabled={analyzingChannel}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Profile Actions */}
        <div className="mb-4 flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            className="text-indigo-600 hover:bg-indigo-50"
            onClick={() => {
              setNeedsOnboarding(true);
              setHasClaimed(false);
            }}
            data-testid="analyze-new-channel-btn"
          >
            <Youtube className="w-4 h-4 mr-1" />
            Analyze New Channel
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-500 hover:text-red-600"
            onClick={async () => {
              if (window.confirm('Are you sure you want to unclaim this channel? You can claim it again or analyze a different channel.')) {
                try {
                  const response = await axios.post(`${API}/admin/reset-user-profile`, {}, { withCredentials: true });
                  if (response.data.success) {
                    toast.success(`Channel "${response.data.unclaimed_channel || 'profile'}" unclaimed!`);
                    setTimeout(() => window.location.reload(), 1000);
                  } else {
                    toast.error(response.data.message || 'No profile to reset');
                  }
                } catch (error) {
                  console.error('Reset error:', error);
                  toast.error(error.response?.data?.detail || 'Failed to reset profile');
                }
              }
            }}
            data-testid="reset-profile-btn"
          >
            <XCircle className="w-4 h-4 mr-1" />
            Unclaim Channel
          </Button>
        </div>

        {/* Demo Mode Banner */}
        {isDemoMode && (
          <Card className="p-6 mb-8 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200" data-testid="demo-mode-banner">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>
                    Demo Sponsorship Report
                  </h3>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                    Sample Data
                  </span>
                </div>
                <p className="text-slate-600 mb-4">
                  You're viewing a demo report with sample data. Connect your YouTube channel to get personalized sponsorship insights, real brand matches, and accurate analytics tailored to your content.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => {
                      localStorage.setItem('creator_mode', 'existing');
                      const redirectUrl = window.location.origin + '/auth/callback';
                      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    }}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="connect-channel-btn"
                  >
                    <Youtube className="w-4 h-4 mr-2" />
                    Connect Your YouTube Channel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open('https://youtube.com', '_blank')}
                    data-testid="create-channel-btn"
                  >
                    Don't have a channel? Create one
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className={`p-6 ${isDemoMode ? 'border-amber-200 bg-amber-50/30' : ''}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDemoMode ? 'bg-amber-100' : 'bg-indigo-100'}`}>
                <TrendingUp className={`w-5 h-5 ${isDemoMode ? 'text-amber-600' : 'text-indigo-600'}`} />
              </div>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                {isDemoMode ? 'Sample Score' : 'Readiness'}
              </p>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}} data-testid="readiness-score">
              {dashboardData?.creator?.sponsorship_readiness_score}%
            </p>
            {isDemoMode && <p className="text-xs text-amber-600 mt-1">Demo data</p>}
          </Card>

          <Card className={`p-6 ${isDemoMode ? 'border-amber-200 bg-amber-50/30' : ''}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDemoMode ? 'bg-amber-100' : 'bg-indigo-100'}`}>
                <Target className={`w-5 h-5 ${isDemoMode ? 'text-amber-600' : 'text-indigo-600'}`} />
              </div>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Brand Matches</p>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}} data-testid="total-matches">
              {dashboardData?.stats?.total_matches || 0}
            </p>
            {isDemoMode && <p className="text-xs text-amber-600 mt-1">Demo data</p>}
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-indigo-600" />
              </div>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Requested</p>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}} data-testid="brands-requested">
              {dashboardData?.stats?.brands_requested || 0}
            </p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Referrals</p>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}} data-testid="referrals-count">
              {dashboardData?.stats?.referrals_made || 0}
            </p>
          </Card>
        </div>

        {/* Top Sponsor Matches */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Top Sponsor Matches</h2>
          <p className="text-sm text-slate-600 mb-6">AI-ranked sponsors based on creator niche relevance and detection confidence from our sponsor intelligence database.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {dashboardData?.matches?.slice(0, 10).map((match, index) => (
              <Card key={match.match_id} className="p-6" data-testid={`brand-match-card-${index}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>{match.brand_name}</h3>
                    <p className="text-sm text-slate-600">{match.industry}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>{match.match_score}%</p>
                    <p className="text-xs text-slate-500">Sponsor Fit Score</p>
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-slate-600 mb-1">Sponsorship Probability</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-600 rounded-full" style={{width: `${match.sponsorship_probability}%`}}></div>
                    </div>
                    <span className="text-sm font-semibold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>{match.sponsorship_probability}%</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => generatePitch(match)}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid={`generate-pitch-btn-${index}`}
                  >
                    Generate Pitch
                  </Button>
                  <Button
                    onClick={() => requestBrand(match.brand_id)}
                    variant="outline"
                    data-testid={`request-collab-btn-${index}`}
                  >
                    Request Collab
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Brands You Should Pitch - High Frequency Sponsors */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>
            <Building2 className="w-6 h-6 inline mr-2 text-emerald-600" />
            Brands You Should Pitch
          </h2>
          <p className="text-slate-600 mb-6">High-frequency sponsors detected across multiple creators in your niche. Score based on sponsorship frequency, niche relevance, and detection confidence.</p>
          
          {loadingBrands ? (
            <div className="py-8 text-center">
              <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600">Finding brands for you...</p>
            </div>
          ) : recommendedBrands.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6">
              {recommendedBrands.map((brand, index) => (
                <Card key={brand.brand_name} className="p-5 border-emerald-100" data-testid={`recommended-brand-${index}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>{brand.brand_name}</h3>
                      <p className="text-sm text-slate-600">{brand.niche_category}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-emerald-600" style={{fontFamily: 'JetBrains Mono'}}>{brand.match_score}%</p>
                      <p className="text-xs text-slate-500">Sponsor Fit Score</p>
                    </div>
                  </div>
                  
                  {/* Sponsorship Probability */}
                  <div className="mb-3">
                    <p className="text-xs text-slate-500 mb-1">Est. Sponsorship Probability</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{width: `${brand.sponsorship_probability}%`}}></div>
                      </div>
                      <span className="text-sm font-semibold text-slate-700" style={{fontFamily: 'JetBrains Mono'}}>{brand.sponsorship_probability}%</span>
                    </div>
                  </div>
                  
                  {/* Match Reason */}
                  <p className="text-sm text-slate-600 mb-3">
                    <span className="font-medium">Why match:</span> {brand.reason_for_match}
                  </p>
                  
                  {/* Expandable Factors */}
                  <button 
                    onClick={() => toggleFactorsExpand(brand.brand_name)}
                    className="flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800 mb-3"
                  >
                    <Info className="w-3 h-3" />
                    {expandedFactors[brand.brand_name] ? 'Hide factors' : 'Show match factors'}
                  </button>
                  
                  {expandedFactors[brand.brand_name] && brand.match_factors && (
                    <div className="mb-3 p-3 bg-emerald-50 rounded-lg space-y-2 text-xs">
                      {Object.entries(brand.match_factors).map(([key, factor]) => (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-emerald-500 rounded-full"
                                style={{width: `${(factor.score / factor.max) * 100}%`}}
                              ></div>
                            </div>
                            <span className="text-slate-700 font-medium w-10 text-right">{factor.score}/{factor.max}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <Button 
                    onClick={() => generatePitchEmail(brand)}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`generate-pitch-btn-${index}`}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Generate Pitch Email
                  </Button>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center" data-testid="no-brands-message">
              {noDataForNiche ? (
                <>
                  <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 font-medium mb-2">No sponsorship data available for this niche yet.</p>
                  <p className="text-slate-500 text-sm">Our discovery engine is analyzing more creators.</p>
                </>
              ) : (
                <p className="text-slate-500">No brand recommendations yet. Continue building your channel!</p>
              )}
            </Card>
          )}
          
          {/* Brand Disclaimer */}
          <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-100">
            <BrandDisclaimerInline />
          </div>
        </div>

        {/* Campaigns */}
        {campaigns.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-6" style={{fontFamily: 'Manrope'}}>Active Sponsorship Opportunities</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {campaigns.map((campaign) => {
                const hasApplied = applications.some(app => app.campaign_id === campaign.campaign_id);
                const application = applications.find(app => app.campaign_id === campaign.campaign_id);
                
                return (
                  <Card key={campaign.campaign_id} className="p-6 bg-gradient-to-br from-indigo-50 to-slate-50" data-testid={`campaign-${campaign.campaign_id}`}>
                    <div className="mb-4">
                      <h3 className="text-xl font-semibold text-slate-900 mb-1" style={{fontFamily: 'Manrope'}}>{campaign.campaign_title}</h3>
                      <p className="text-sm font-medium text-indigo-600">{campaign.brand_name}</p>
                    </div>
                    <p className="text-sm text-slate-600 mb-4">{campaign.campaign_description}</p>
                    
                    {campaign.deliverables && (
                      <div className="mb-4 p-3 bg-white rounded-lg">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Deliverables</p>
                        <p className="text-sm text-slate-700">{campaign.deliverables}</p>
                      </div>
                    )}
                    
                    {campaign.estimated_budget_min && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Budget Range</p>
                        <p className="text-lg font-semibold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                          ${campaign.estimated_budget_min.toLocaleString()} - ${campaign.estimated_budget_max.toLocaleString()}
                        </p>
                      </div>
                    )}
                    
                    {hasApplied ? (
                      <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-slate-900">Application Submitted</p>
                          <p className="text-xs text-slate-600">Status: <span className="font-medium capitalize">{application?.status}</span></p>
                        </div>
                      </div>
                    ) : (
                      <Button 
                        onClick={() => openApplicationModal(campaign)}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid={`apply-btn-${campaign.campaign_id}`}
                      >
                        Apply to Campaign
                      </Button>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Weekly Sponsorship Insights - Data-Driven */}
        {weeklyInsights && (
          <div className="mb-8" data-testid="weekly-insights-section">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-6" style={{fontFamily: 'Manrope'}}>
              Weekly Sponsorship Insights
            </h2>
            <p className="text-slate-600 mb-6">
              Real-time sponsorship intelligence for {weeklyInsights.niche || dashboardData?.creator?.niche}
              {weeklyInsights.insights_period && (
                <span className="text-sm text-slate-500 ml-2">
                  ({weeklyInsights.insights_period.start} to {weeklyInsights.insights_period.end})
                </span>
              )}
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {/* Top Sponsors Card */}
              <Card className="p-5 bg-gradient-to-br from-indigo-50 to-slate-50" data-testid="top-sponsors-card">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="h-5 w-5 text-indigo-600" />
                  <h3 className="text-base font-semibold text-slate-900">Top Sponsors in Your Niche</h3>
                </div>
                <div className="space-y-3">
                  {weeklyInsights.top_sponsoring_brands?.slice(0, 5).map((brand, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-slate-700 font-medium">{brand.brand}</span>
                      <Badge variant="secondary" className="text-xs bg-indigo-100 text-indigo-700">
                        {brand.mentions} mentions
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>
              
              {/* New Brands This Week */}
              <Card className="p-5 bg-gradient-to-br from-green-50 to-slate-50" data-testid="new-brands-card">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="h-5 w-5 text-green-600" />
                  <h3 className="text-base font-semibold text-slate-900">New Brands This Week</h3>
                </div>
                {weeklyInsights.new_brands_detected_this_week?.length > 0 ? (
                  <div className="space-y-2">
                    {weeklyInsights.new_brands_detected_this_week.slice(0, 5).map((brand, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        <span className="text-sm text-slate-700">{brand.brand || brand}</span>
                        {brand.category && (
                          <span className="text-xs text-slate-500">({brand.category})</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Our discovery engine is continuously analyzing creator sponsorships. New brands will appear here as they are detected.</p>
                )}
              </Card>
              
              {/* Trending Categories */}
              <Card className="p-5 bg-gradient-to-br from-purple-50 to-slate-50" data-testid="trending-categories-card">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                  <h3 className="text-base font-semibold text-slate-900">Trending Categories</h3>
                </div>
                <div className="space-y-2">
                  {weeklyInsights.trending_categories?.slice(0, 5).map((cat, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className={`text-sm ${cat.is_your_niche ? 'text-purple-700 font-semibold' : 'text-slate-700'}`}>
                        {cat.category}
                        {cat.is_your_niche && <span className="ml-1 text-xs">(your niche)</span>}
                      </span>
                      <span className="text-xs text-slate-500">{cat.total_mentions || cat.brand_count} mentions</span>
                    </div>
                  ))}
                </div>
              </Card>
              
              {/* Brands to Pitch */}
              <Card className="p-5 bg-gradient-to-br from-amber-50 to-slate-50" data-testid="pitch-recommendations-card">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="h-5 w-5 text-amber-600" />
                  <h3 className="text-base font-semibold text-slate-900">Brands You Should Pitch</h3>
                </div>
                <div className="space-y-3">
                  {weeklyInsights.recommended_brands_to_pitch?.slice(0, 5).map((brand, i) => (
                    <div key={i} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-700 font-medium">{brand.brand}</span>
                        <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                          Fit: {brand.pitch_score}
                        </Badge>
                      </div>
                      {brand.reason && (
                        <p className="text-xs text-slate-500">{brand.reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </div>
            
            {/* Niche Stats Summary */}
            {weeklyInsights.niche_stats && (
              <Card className="p-5 bg-slate-50" data-testid="niche-stats-card">
                <h3 className="text-base font-semibold text-slate-900 mb-4">Your Niche at a Glance</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-indigo-600">{weeklyInsights.niche_stats.total_brands_in_niche}</p>
                    <p className="text-xs text-slate-600">Active Sponsors</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{weeklyInsights.niche_stats.total_mentions_in_niche}</p>
                    <p className="text-xs text-slate-600">Total Mentions</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{weeklyInsights.niche_stats.avg_mentions_per_brand}</p>
                    <p className="text-xs text-slate-600">Avg Mentions/Brand</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{weeklyInsights.niche_stats.total_creators_sponsoring}</p>
                    <p className="text-xs text-slate-600">Creators Sponsored</p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}
        
        {loadingWeeklyInsights && (
          <div className="mb-8">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-6" style={{fontFamily: 'Manrope'}}>
              Weekly Sponsorship Insights
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1,2,3,4].map(i => (
                <Card key={i} className="p-5 animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-3/4 mb-4"></div>
                  <div className="space-y-2">
                    <div className="h-3 bg-slate-200 rounded"></div>
                    <div className="h-3 bg-slate-200 rounded w-5/6"></div>
                    <div className="h-3 bg-slate-200 rounded w-4/6"></div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Legacy Weekly Insights - keep as fallback */}
        {insights && !weeklyInsights && (
          <div className="mb-8">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-6" style={{fontFamily: 'Manrope'}}>Weekly Insights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Trending Topics</h3>
                <ul className="space-y-2">
                  {insights.trending_topics?.map((topic, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700">
                      <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                      {topic}
                    </li>
                  ))}
                </ul>
              </Card>
              
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Sponsorship Trends</h3>
                <p className="text-slate-600">{insights.sponsorship_trends}</p>
              </Card>
            </div>
          </div>
        )}

        {/* Outreach Tracker */}
        <div className="mb-8" data-testid="outreach-tracker-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900" style={{fontFamily: 'Manrope'}}>
                <Mail className="w-6 h-6 inline mr-2 text-blue-600" />
                Outreach Tracker
              </h2>
              <p className="text-sm text-slate-600 mt-1">Track your sponsorship outreach and deal progress</p>
            </div>
            <Button 
              onClick={() => setShowAddOutreach(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="add-outreach-btn"
            >
              + Add Outreach
            </Button>
          </div>
          
          {/* Add Outreach Form */}
          {showAddOutreach && (
            <Card className="p-5 mb-6 border-blue-200 bg-blue-50/30">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Add New Outreach</h3>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-sm text-slate-700">Brand Name *</Label>
                  <Input 
                    value={newOutreach.brand}
                    onChange={(e) => setNewOutreach({...newOutreach, brand: e.target.value})}
                    placeholder="e.g., NordVPN"
                    className="mt-1"
                    data-testid="outreach-brand-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Status</Label>
                  <Select 
                    value={newOutreach.status}
                    onValueChange={(v) => setNewOutreach({...newOutreach, status: v})}
                  >
                    <SelectTrigger className="mt-1" data-testid="outreach-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="to_pitch">To Pitch</SelectItem>
                      <SelectItem value="pitch_sent">Pitch Sent</SelectItem>
                      <SelectItem value="negotiation">Negotiation</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Deal Value (optional)</Label>
                  <Input 
                    value={newOutreach.dealValue}
                    onChange={(e) => setNewOutreach({...newOutreach, dealValue: e.target.value})}
                    placeholder="e.g., $1,500"
                    className="mt-1"
                    data-testid="outreach-value-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Notes (optional)</Label>
                  <Input 
                    value={newOutreach.notes}
                    onChange={(e) => setNewOutreach({...newOutreach, notes: e.target.value})}
                    placeholder="Any additional notes"
                    className="mt-1"
                    data-testid="outreach-notes-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={addOutreach} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-outreach-btn">
                  Save Outreach
                </Button>
                <Button onClick={() => setShowAddOutreach(false)} variant="outline">
                  Cancel
                </Button>
              </div>
            </Card>
          )}
          
          {/* Outreach Table */}
          {outreachList.length > 0 ? (
            <Card className="p-0 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Brand</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Deal Value</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Notes</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {outreachList.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50" data-testid={`outreach-row-${item.id}`}>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-900">{item.brand}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Select 
                          value={item.status}
                          onValueChange={(v) => updateOutreachStatus(item.id, v)}
                        >
                          <SelectTrigger className={`w-32 h-8 text-xs ${getStatusColor(item.status)}`}>
                            <SelectValue>{getStatusLabel(item.status)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="to_pitch">To Pitch</SelectItem>
                            <SelectItem value="pitch_sent">Pitch Sent</SelectItem>
                            <SelectItem value="negotiation">Negotiation</SelectItem>
                            <SelectItem value="accepted">Accepted</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{item.dealValue || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.notes || '-'}</td>
                      <td className="px-4 py-3">
                        <Button 
                          onClick={() => deleteOutreach(item.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : (
            <Card className="p-8 text-center bg-slate-50">
              <Mail className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Outreach Yet</h3>
              <p className="text-slate-600 mb-4">Start tracking your sponsorship outreach by adding brands you want to pitch.</p>
              <Button 
                onClick={() => setShowAddOutreach(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Add Your First Outreach
              </Button>
            </Card>
          )}
        </div>

        {/* Content Ideas */}
        {contentIdeas.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-6" style={{fontFamily: 'Manrope'}}>AI-Generated Content Ideas</h2>
            <Card className="p-6">
              <p className="text-slate-600 mb-4">Video ideas to attract brand sponsorships:</p>
              <ul className="space-y-3">
                {contentIdeas.map((idea, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-semibold">
                      {i + 1}
                    </span>
                    <span className="text-slate-900">{idea}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        )}

        {/* Viral Features & Referral Section */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Viral Report */}
          <Card className="p-6 bg-gradient-to-br from-purple-50 to-indigo-50">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Share Your Report</h2>
            <p className="text-slate-600 mb-4">Generate a viral sponsorship report to share on social media</p>
            <Link to={`/report/${dashboardData?.creator?.creator_id}`} target="_blank">
              <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="view-viral-report-btn">
                View & Share Report
              </Button>
            </Link>
          </Card>

          {/* Sponsorship Badge */}
          <Card className="p-6 bg-gradient-to-br from-indigo-50 to-slate-50">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Sponsorship Badge</h2>
            <p className="text-slate-600 mb-4">Embed your sponsorship score on your website or YouTube</p>
            <Button
              onClick={() => setShowBadgeModal(true)}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="get-badge-btn"
            >
              Get Badge Code
            </Button>
          </Card>
        </div>

        {/* Referral Section */}
        <Card className="p-6">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Invite Other Creators</h2>
          <p className="text-slate-600 mb-4">Invite creators and unlock additional brand insights when they join.</p>
          <div className="flex gap-4">
            <Input
              type="email"
              placeholder="Enter creator's email"
              value={referralEmail}
              onChange={(e) => setReferralEmail(e.target.value)}
              className="flex-1"
              data-testid="referral-email-input"
            />
            <Button onClick={sendReferral} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="send-referral-btn">
              Send Invite
            </Button>
          </div>
          <div className="mt-4">
            <p className="text-sm text-slate-600">Your referral code: <span className="font-semibold" style={{fontFamily: 'JetBrains Mono'}}>{dashboardData?.creator?.referral_code}</span></p>
          </div>
        </Card>
      </div>

      {/* Pitch Generation Modal */}
      <Dialog open={showPitchModal} onOpenChange={setShowPitchModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Manrope'}}>AI-Generated Pitch for {selectedBrand?.brand_name}</DialogTitle>
            <DialogDescription>Review and customize this pitch before sending to the brand.</DialogDescription>
          </DialogHeader>
          {generatingPitch ? (
            <div className="py-8 text-center">
              <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600">Generating personalized pitch...</p>
            </div>
          ) : (
            <div>
              <Textarea
                value={generatedPitch}
                onChange={(e) => setGeneratedPitch(e.target.value)}
                className="min-h-[300px] mb-4"
                data-testid="pitch-textarea"
              />
              <div className="flex gap-2">
                <Button onClick={copyPitch} variant="outline" className="flex-1" data-testid="copy-pitch-btn">
                  <Copy className="w-4 h-4 mr-2" />
                  Copy to Clipboard
                </Button>
                <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="send-email-btn">
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Badge Generator Modal */}
      <Dialog open={showBadgeModal} onOpenChange={setShowBadgeModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Manrope'}}>Your Sponsorship Badge</DialogTitle>
            <DialogDescription>Copy the embed code to add your badge to your website or YouTube description</DialogDescription>
          </DialogHeader>
          {badgeData && (
            <div className="space-y-6">
              {/* Badge Preview */}
              <div className="text-center p-6 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600 mb-4">Badge Preview</p>
                <div dangerouslySetInnerHTML={{ __html: badgeData.svg }} className="inline-block" />
              </div>

              {/* HTML Code */}
              <div>
                <Label className="mb-2 block">HTML Code</Label>
                <div className="relative">
                  <Textarea
                    value={badgeData.embed_html}
                    readOnly
                    className="font-mono text-xs h-24"
                    data-testid="badge-html"
                  />
                  <Button
                    onClick={() => copyBadgeCode(badgeData.embed_html)}
                    size="sm"
                    className="absolute top-2 right-2"
                    data-testid="copy-html-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Markdown Code */}
              <div>
                <Label className="mb-2 block">Markdown Code</Label>
                <div className="relative">
                  <Textarea
                    value={badgeData.embed_markdown}
                    readOnly
                    className="font-mono text-xs h-20"
                    data-testid="badge-markdown"
                  />
                  <Button
                    onClick={() => copyBadgeCode(badgeData.embed_markdown)}
                    size="sm"
                    className="absolute top-2 right-2"
                    data-testid="copy-markdown-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Instructions */}
              <div className="p-4 bg-indigo-50 rounded-lg">
                <p className="text-sm text-slate-700 font-medium mb-2">How to use:</p>
                <ul className="text-sm text-slate-600 space-y-1 ml-4">
                  <li>• Copy the HTML code for your website</li>
                  <li>• Copy the Markdown code for GitHub README</li>
                  <li>• Badge links back to your public profile</li>
                  <li>• Updates automatically as your score changes</li>
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Campaign Application Modal */}
      <Dialog open={showApplicationModal} onOpenChange={setShowApplicationModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Manrope'}}>Apply to {selectedCampaign?.campaign_title}</DialogTitle>
            <DialogDescription>Write a pitch explaining why you're a great fit for this campaign</DialogDescription>
          </DialogHeader>
          {selectedCampaign && (
            <div className="space-y-6">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">{selectedCampaign.brand_name}</p>
                <p className="text-sm text-slate-600 mb-3">{selectedCampaign.campaign_description}</p>
                {selectedCampaign.deliverables && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Deliverables</p>
                    <p className="text-sm text-slate-700">{selectedCampaign.deliverables}</p>
                  </div>
                )}
              </div>

              <div>
                <Label className="mb-2 block">Your Pitch *</Label>
                <Textarea
                  value={applicationPitch}
                  onChange={(e) => setApplicationPitch(e.target.value)}
                  placeholder="Explain why you're the perfect creator for this campaign. Include your relevant experience, audience demographics, and creative ideas..."
                  className="min-h-[200px]"
                  data-testid="application-pitch-textarea"
                />
                <p className="text-sm text-slate-500 mt-2">Be specific about how you'll deliver value to the brand</p>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowApplicationModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={submitApplication}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="submit-application-btn"
                >
                  Submit Application
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pitch Email Modal */}
      <Dialog open={showPitchModal} onOpenChange={setShowPitchModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Manrope'}}>
              <Send className="w-5 h-5 inline mr-2 text-emerald-600" />
              Pitch Email for {selectedBrand?.brand_name}
            </DialogTitle>
            <DialogDescription>Copy this email and send it to {selectedBrand?.brand_name}</DialogDescription>
          </DialogHeader>
          
          {generatingPitch ? (
            <div className="py-12 text-center">
              <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600">Generating your personalized pitch...</p>
            </div>
          ) : generatedPitch ? (
            <div className="space-y-4">
              <div>
                <Label className="mb-2 block text-sm text-slate-500">Subject Line</Label>
                <div className="relative">
                  <Input 
                    value={generatedPitch.subject || ''} 
                    readOnly 
                    className="pr-12 bg-slate-50"
                    data-testid="pitch-subject"
                  />
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedPitch.subject);
                      toast.success('Subject copied!');
                    }}
                    size="sm"
                    variant="ghost"
                    className="absolute top-1 right-1"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div>
                <Label className="mb-2 block text-sm text-slate-500">Email Body</Label>
                <div className="relative">
                  <Textarea 
                    value={generatedPitch.body || ''} 
                    readOnly 
                    className="min-h-[300px] bg-slate-50 font-mono text-sm"
                    data-testid="pitch-body"
                  />
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedPitch.body);
                      toast.success('Body copied!');
                    }}
                    size="sm"
                    variant="ghost"
                    className="absolute top-2 right-2"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  onClick={copyPitchToClipboard}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="copy-full-pitch-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Full Email
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowPitchModal(false)}
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
              
              <p className="text-xs text-slate-500 text-center">
                Tip: Personalize this email further before sending to increase response rates!
              </p>
            </div>
          ) : (
            <div className="py-8 text-center text-slate-500">
              <p>Failed to generate pitch. Please try again.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Platform Disclaimer */}
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <PlatformDisclaimer />
      </div>
      
      {/* Footer */}
      <Footer showDisclaimer={false} />
    </div>
  );
};

export default CreatorDashboard;
